<template>
  <HousemateGroupModal
    v-if="open"
    :show="open"
    :show-close-icon="true"
    :hide-actions="!isLoggedInStudent"
    dynamic-body-class="md:tw-py-[24px] md:tw-px-[24px] tw-px-[24px] tw-py-[16px]"
    @close-modal="handleClose"
  >
    <template #header>
      <span>{{ title }}</span>
    </template>

    <template #body>
      <template v-if="!isLoggedInStudent">
        <div class="tw-flex tw-flex-col tw-items-center tw-justify-center">
          <div class="tw-aspect-[400/273] tw-w-full tw-max-w-[400px]">
            <img
              :src="cdnAsset('images/student/groups-empty.png')"
              alt=""
              loading="lazy"
              decoding="async"
              class="tw-block tw-h-full tw-w-full tw-object-contain"
            />
          </div>

          <div
            class="tw-font-inter tw-line-clamp-1 tw-self-stretch tw-overflow-hidden tw-text-ellipsis tw-py-[8px] tw-text-center tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143]"
          >
            You need to login or create a profile
          </div>

          <div
            class="tw-font-inter tw-pb-[24px] tw-text-center tw-text-[14px] tw-font-normal tw-leading-[150%] tw-text-[#6B7280]"
          >
            Grouplists are a way to collaborate on a property shortlist and
            explore options together! Stay in the know and make important
            decisions as a team.
          </div>

          <a
            href="/account/login"
            class="tw-mx-auto tw-flex tw-w-full tw-max-w-[328px] tw-flex-1 tw-items-center tw-justify-center tw-gap-[8px] tw-self-stretch tw-rounded-[30px] tw-bg-[#F48831] tw-px-[24px] tw-py-[8px] hover:tw-bg-[#E16B0C] focus-visible:tw-bg-[#E16B0C] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 disabled:tw-cursor-not-allowed disabled:tw-bg-[#efefef] disabled:hover:tw-bg-[#efefef]"
          >
            <span
              class="tw-font-inter tw-text-center tw-text-[16px] tw-font-bold tw-leading-[150%] tw-text-white"
            >
              Log in
            </span>
          </a>

          <span class="tw-mt-[16px] tw-text-[16px]">
            Don't have a profile?

            <a href="/account/sign-up" class="tw-font-[700] hover:tw-text-[#0090C8] focus-visible:tw-text-[#0090C8] focus-visible:tw-rounded-[2px] focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"> Sign up </a>
          </span>
        </div>
      </template>

      <template v-else-if="mode === 'choose'">
        <button
          v-if="showPersonalShortlistOption"
          type="button"
          class="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-[12px] tw-rounded-[16px] tw-border tw-border-[#E5E7EB] tw-bg-white tw-px-[16px] tw-py-[14px] tw-text-left hover:tw-border-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 md:tw-w-[calc(50%-8px)]"
          :disabled="saving"
          @click="selectPersonal"
        >
          <div class="tw-min-w-0">
            <div
              class="tw-font-inter tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143]"
            >
              Personal shortlist
            </div>
            <div
              class="tw-font-inter tw-text-[14px] tw-font-normal tw-leading-[150%] tw-text-[#6B7280]"
            >
              Save just for you
            </div>
          </div>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            class="tw-flex-shrink-0 tw-text-[#9CA3AF]"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              stroke-width="2.25"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <button
          v-for="group in approvedGroups"
          :key="group.housemate_group_id"
          type="button"
          class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-[12px] tw-rounded-[16px] tw-border tw-border-[#E5E7EB] tw-bg-white tw-px-[16px] tw-py-[14px] tw-text-left hover:tw-border-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2 md:tw-w-[calc(50%-8px)]"
          :disabled="saving"
          @click="selectGroup(group.housemate_group_id)"
        >
          <div class="tw-min-w-0">
            <div
              class="tw-font-inter tw-truncate tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143]"
            >
              {{ group.housemate_group_name || 'Grouplist' }}
            </div>
            <div
              class="tw-font-inter tw-text-[14px] tw-font-normal tw-leading-[150%] tw-text-[#6B7280]"
            >
              Save to a shared grouplist
            </div>
          </div>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            class="tw-flex-shrink-0 tw-text-[#9CA3AF]"
          >
            <path
              d="M9 18L15 12L9 6"
              stroke="currentColor"
              stroke-width="2.25"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>

        <label
          v-if="allowSetAsDefault"
          class="tw-font-inter tw-mt-[8px] tw-flex tw-w-full tw-cursor-pointer tw-items-start tw-gap-[10px] tw-rounded-[16px] tw-border tw-border-[#E5E7EB] tw-bg-[#F9FAFB] tw-px-[16px] tw-py-[14px] md:tw-w-full"
        >
          <input
            v-model="setAsDefault"
            type="checkbox"
            class="tw-mt-[2px] tw-h-[18px] tw-w-[18px] tw-rounded tw-border-[#D1D5DB]"
            :disabled="saving"
          />
          <span class="tw-min-w-0">
            <span
              class="tw-block tw-text-[14px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143]"
            >
              Set selection as default
            </span>
            <span
              class="tw-block tw-text-[14px] tw-font-normal tw-leading-[150%] tw-text-[#6B7280]"
            >
              You can change this at any time in your profile.
            </span>
          </span>
        </label>
      </template>

      <template v-else-if="mode === 'create'">
        <div class="tw-w-full">
          <div
            class="tw-font-inter tw-mb-[6px] tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143]"
          >
            Create a group
          </div>
          <div
            class="tw-font-inter tw-mb-[12px] tw-text-[14px] tw-font-normal tw-leading-[150%] tw-text-[#6B7280]"
          >
            Add a new grouplist, then choose it from the list.
          </div>

          <input
            ref="groupNameInput"
            v-model="groupName"
            type="text"
            placeholder="e.g. Grouplist 1"
            class="tw-w-full tw-rounded-[12px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-[14px] tw-py-[12px] tw-text-[16px] tw-leading-[24px] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
            :disabled="saving"
            @keydown.enter.prevent="createGroup"
          />

          <p
            v-if="
              studentStore.hasGroupCreateError &&
              studentStore.groupCreateErrorMsg
            "
            class="tw-font-inter tw-mb-0 tw-mt-[10px] tw-text-[14px] tw-font-normal tw-leading-[150%] tw-text-[#DC2626]"
          >
            {{ studentStore.groupCreateErrorMsg }}
          </p>
        </div>
      </template>

      <template v-else>
        <div class="tw-w-full">
          <div
            class="tw-font-inter tw-mb-[6px] tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143]"
          >
            Share your grouplist
          </div>

          <div
            class="tw-font-inter tw-mb-[12px] tw-text-[14px] tw-font-normal tw-leading-[150%] tw-text-[#6B7280]"
          >
            Your shortlist has been merged into the new grouplist. Share this
            invite link with your housemates.
          </div>

          <p
            v-if="shareStatusMessage"
            class="tw-font-inter tw-mb-0 tw-text-[14px] tw-leading-[150%] tw-text-[#6B7280]"
          >
            {{ shareStatusMessage }}
          </p>
        </div>
      </template>
    </template>

    <template #actions>
      <button
        type="button"
        class="tw-font-inter tw-flex tw-h-[50px] tw-items-center tw-justify-center tw-rounded-[30px] tw-bg-white tw-px-[24px] tw-py-[8px] tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143] hover:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5]"
        :disabled="saving"
        @click="handleCancel"
      >
        {{ mode === 'create' ? 'Back' : 'Cancel' }}
      </button>

      <button
        v-if="mode === 'choose'"
        type="button"
        class="tw-font-inter tw-group tw-flex tw-h-[50px] tw-max-w-[292px] tw-flex-1 tw-items-center tw-justify-center tw-gap-[10px] tw-rounded-[30px] tw-border tw-border-[#D1D5DB] tw-bg-white tw-px-[24px] tw-py-[8px] tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-[#3B4143] hover:tw-border-uhblue-700 hover:tw-text-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
        :disabled="saving"
        @click="mode = 'create'"
      >
        <Users class="tw-text-[#3B4143] group-hover:tw-text-uhblue-700" />
        Create a group
      </button>

      <button
        v-else-if="mode === 'create'"
        type="button"
        class="tw-font-inter tw-flex tw-h-[50px] tw-flex-1 tw-items-center tw-justify-center tw-rounded-[30px] tw-bg-[#00A2E1] tw-px-[24px] tw-py-[8px] tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-white hover:tw-bg-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
        :disabled="saving || !canCreateGroup"
        @click="createGroup"
      >
        Create
      </button>

      <button
        v-else
        type="button"
        class="tw-font-inter tw-flex tw-h-[50px] tw-flex-1 tw-items-center tw-justify-center tw-rounded-[30px] tw-bg-[#00A2E1] tw-px-[24px] tw-py-[8px] tw-text-[16px] tw-font-semibold tw-leading-[150%] tw-text-white hover:tw-bg-uhblue-700 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[#61B3E5] focus-visible:tw-ring-offset-2"
        :disabled="saving || !groupShareUrl"
        @click="handleShareGroup"
      >
        Share group link
      </button>
    </template>
  </HousemateGroupModal>
</template>

<script>
import { useStudentStore } from '@js/stores/student/student';
import HousemateGroupModal from '@components/Student/Modal/HousemateGroupModal.vue';
import Users from '@components/Student/SVG/Users.vue';

export default {
  name: 'SaveToShortlistModal',
  components: { Users, HousemateGroupModal },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    propertyId: {
      type: [Number, String],
      default: null,
    },
    propertyIds: {
      type: Array,
      default: null,
    },
    showPersonalShortlistOption: {
      type: Boolean,
      default: true,
    },
    allowSetAsDefault: {
      type: Boolean,
      default: true,
    },
    title: {
      type: String,
      default: 'Save to shortlist',
    },
    initialMode: {
      type: String,
      default: 'choose',
    },
    showShareStep: {
      type: Boolean,
      default: false,
    },
    isLoggedInStudent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      studentStore: useStudentStore(),
      setAsDefault: false,
      saving: false,
      mode: 'choose',
      groupName: '',
      groupShareUrl: null,
      shareStatusMessage: '',
    };
  },
  computed: {
    targetPropertyIds() {
      if (Array.isArray(this.propertyIds) && this.propertyIds.length) {
        return this.propertyIds
          .map((id) => String(id || '').trim())
          .filter(Boolean);
      }

      const singleId = String(this.propertyId || '').trim();
      return singleId ? [singleId] : [];
    },
    approvedGroups() {
      const groups = Array.isArray(this.studentStore.studentHousemateGroups)
        ? this.studentStore.studentHousemateGroups
        : [];

      return groups.filter((group) => group?.status === 'APPROVED');
    },
    canCreateGroup() {
      return String(this.groupName || '').trim().length > 0;
    },
  },
  watch: {
    open: {
      immediate: true,
      async handler(isOpen) {
        if (!isOpen) {
          return;
        }

        this.mode = this.initialMode === 'create' ? 'create' : 'choose';
        this.groupName = '';
        this.setAsDefault = false;
        this.groupShareUrl = null;
        this.shareStatusMessage = '';
        this.studentStore.clearError();

        if (
          !Array.isArray(this.studentStore.studentHousemateGroups) ||
          !this.studentStore.studentHousemateGroups.length
        ) {
          await this.studentStore.getHousemateGroups();
        }

        // Only fetch persisted settings when the "set as default" UI is shown.
        if (this.allowSetAsDefault) {
          await this.studentStore.getHousemateGroupShortlistSettings();
        }

        this.$nextTick(() => {
          if (this.mode === 'create') {
            this.$refs.groupNameInput?.focus();
          }
        });
      },
    },
    mode(newMode) {
      if (!this.open) {
        return;
      }

      if (newMode !== 'create') {
        return;
      }

      this.$nextTick(() => {
        this.$refs.groupNameInput?.focus();
      });
    },
  },
  methods: {
    handleClose() {
      if (this.saving) {
        return;
      }
      this.$emit('close');
    },
    handleCancel() {
      if (this.saving) {
        return;
      }

      if (this.mode === 'create' && !this.showShareStep) {
        this.mode = 'choose';
        return;
      }

      if (this.mode === 'create' && this.showShareStep) {
        this.$emit('close');
        return;
      }

      this.$emit('close');
    },
    async selectPersonal() {
      if (this.saving) {
        return;
      }

      if (!this.targetPropertyIds.length) {
        return;
      }

      this.saving = true;
      try {
        for (const propertyId of this.targetPropertyIds) {
          await this.studentStore.postAddToShortlist(propertyId);
        }

        if (this.allowSetAsDefault && this.setAsDefault) {
          await this.studentStore.postUpdateHousemateGroupShortlistSettings({
            enableDefault: true,
            destination: 'personal',
            defaultHousemateGroupId: null,
          });
        }

        this.$emit('saved', { destination: 'personal' });
        this.$emit('close');
      } finally {
        this.saving = false;
      }
    },
    async selectGroup(groupId, { allowWhileSaving = false } = {}) {
      if ((this.saving && !allowWhileSaving) || !groupId) {
        return;
      }

      if (!this.targetPropertyIds.length) {
        return;
      }

      this.saving = true;
      try {
        const resolvedGroupId = String(groupId || '').trim();

        if (!resolvedGroupId) {
          return;
        }

        const setAsDefault = this.allowSetAsDefault && !!this.setAsDefault;

        if (this.targetPropertyIds.length > 1) {
          await this.studentStore.addMultipleToHousemateGroupShortlist({
            housemateGroupId: resolvedGroupId,
            propertyIds: this.targetPropertyIds,
            setAsDefault,
          });
        } else {
          await this.studentStore.addToHousemateGroupShortlist({
            housemateGroupId: resolvedGroupId,
            propertyId: this.targetPropertyIds[0],
            setAsDefault,
          });
        }

        this.$emit('saved', { destination: 'group', groupId: resolvedGroupId });

        if (this.showShareStep) {
          this.groupShareUrl = this.getGroupShareUrl(resolvedGroupId);
          this.mode = 'share';
          return;
        }

        this.$emit('close');
      } finally {
        this.saving = false;
      }
    },
    async createGroup() {
      if (this.saving || !this.canCreateGroup) {
        return;
      }

      this.saving = true;
      try {
        const createdGroupId = await this.studentStore.postHousemateGroup(
          String(this.groupName).trim()
        );
        await this.studentStore.getHousemateGroups();

        if (this.showShareStep && createdGroupId) {
          this.groupName = '';
          await this.selectGroup(createdGroupId, { allowWhileSaving: true });
          return;
        }

        this.mode = 'choose';
        this.groupName = '';
      } finally {
        this.saving = false;
      }
    },
    getGroupShareUrl(groupId) {
      const groups = Array.isArray(this.studentStore.studentHousemateGroups)
        ? this.studentStore.studentHousemateGroups
        : [];

      const group = groups.find(
        (item) => String(item?.housemate_group_id || '') === String(groupId)
      );

      return group?.housemate_group_open_invite_link ?? null;
    },
    async handleShareGroup() {
      if (this.saving || !this.groupShareUrl) {
        return;
      }

      this.shareStatusMessage = '';

      try {
        if (navigator.share) {
          await navigator.share({
            title: 'UniHomes',
            text: `Join my housemate group using this link:\n${this.groupShareUrl}`,
            url: this.groupShareUrl,
          });

          this.shareStatusMessage = 'Shared';
          return;
        }

        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(this.groupShareUrl);
          this.shareStatusMessage = 'Link copied';
          return;
        }

        this.shareStatusMessage = 'Unable to share on this device';
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        this.shareStatusMessage = 'Error sharing link';
      }
    },
  },
};
</script>
